/**
 * WriteGuard - Conditionally renders children based on write permission.
 * 
 * Usage:
 *   <WriteGuard page="contacts">
 *     <Button>Add Contact</Button>
 *   </WriteGuard>
 * 
 * If the user has write access to the page (or is a super admin), children render normally.
 * If the user only has read access, children are hidden (or optionally rendered as disabled).
 * 
 * Props:
 *   page     - The page key to check (e.g. 'contacts', 'templates', 'campaigns')
 *   fallback - Optional: what to render when user lacks write access (default: null)
 *   disable  - Optional: if true, renders children wrapped in a disabled container instead of hiding
 */
import { useUserContext, canWrite } from '../context/userContext';

const WriteGuard = ({ page, children, fallback = null, disable = false }) => {
  const { userCtx } = useUserContext();

  // If context isn't loaded yet or user has no page restrictions, allow through
  if (!userCtx || !userCtx.pages) return children;

  if (canWrite(userCtx, page)) return children;

  // User has read-only access
  if (disable) {
    return (
      <span style={{ pointerEvents: 'none', opacity: 0.5 }}>
        {children}
      </span>
    );
  }

  return fallback;
};

export default WriteGuard;
